import { InputAdornment } from "@mui/material";
import _ from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import images from "../assets/images";
import SelectProductAutocomplete from "../common/selectProduct/ProductAutocomplete";
import Aggrid from "../framework/AgGrid";
import Autocomplete from "../framework/Autocomplete";
import Button from "../framework/Button";
import CellNumberEditor from "../framework/cellEditor/CellNumberEditor";
import CellTextEditor from "../framework/cellEditor/CellTextEditor";
import GlobalContext from "../framework/GlobalContext";
import Shortcut from "../framework/Shortcut";
import { displayToast } from "../framework/toast/toastUtils";
import {
  fetchBatchByBarcode,
  searchBatchByIdView,
  searchBatchInPurchase,
} from "../services/batch-services";
import {
  computeStock,
  getMedicineDetails,
} from "../services/medicine-services";
import { MIN_BARCODE_LENGTH } from "../utils/constant";
import {
  cellClicked,
  cellTabbed,
  focusCell,
  initializeColumnDefs,
  rowDown,
  rowUp,
  selectRow,
  stopEditing,
} from "../utils/gridUtils";
import { getStringValue } from "../utils/utils";
import {
  STOCK_AUDIT_COLUMN_DEFS,
  stock_audit_col_defs,
} from "./stockAuditConstants";
import StockAuditContext from "./StockAuditContext";
import classes from "./StockAuditProductGrid.module.css";
import { createProductData } from "./stockAuditUtils";

const StockAuditProductGrid = () => {
  const [api, setApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [showMedicineAutocomplete, setShowMedicineAutocomplete] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [lastScannedMedicine, setLastScannedMedicine] = useState(null);
  const dispatch = useDispatch();
  const { medicineGridRef, showAuditorDetails } = useContext(StockAuditContext);
  const {
    autocompleteOpen,
    currentComponent,
    setCurrentComponent,
    barcode,
    setBarcode,
    focusField,
    setShowLoading,
  } = useContext(GlobalContext);
  const {
    lastProductSelectedIndex,
    productRowData,
    actors: { startDate },
  } = useSelector(
    (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
  );
  const {
    IS_SELL_IN_LOOSE,
    MAX_SALES_QUANTITY,
    SHOW_CURRENT_STOCK_IN_STOCK_AUDIT,
  } = useSelector((state) => state.commonReducer.configData);
  const { searchIcon } = images;
  const isBarcode = (value) => !isNaN(value) && value.indexOf(" ") === -1;
  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
    setApi(params.api);
    setColumnApi(params.columnApi);
  };
  const defaultColDef = {
    cellStyle: (params) => {
      return {
        fontSize: 14,
        fontWeight: 400,
      };
    },
    cellEditor: "cellTextEditor",
    cellEditorParams: (params) => {
      return {
        value: params?.value || "",
      };
    },
    onCellValueChanged: (params) => {
      cellValueChanged(params.node, params.column);
    },
    cellRendererFramework: (params) => {
      return !params.data.isPromise ? (
        params.colDef.editable ? (
          <div className="ag-editable-cell">{getStringValue(params.value)}</div>
        ) : (
          getStringValue(params.value)
        )
      ) : (
        ""
      );
    },
    suppressKeyboardEvent: (params) => true,
  };
  const withQtyValueCap = useCallback(
    ({ value }) => {
      return value <= +MAX_SALES_QUANTITY && value >= 0;
    },
    [MAX_SALES_QUANTITY]
  );

  const searchMedicine = useCallback(
    (searchTerm) => {
      setShowMedicineAutocomplete(searchTerm?.length > 2);
      setSearchTerm(searchTerm);
      setCurrentComponent(
        searchTerm?.length > 2 ? "grid_medicine_search" : "grid_stock_audit"
      );
    },
    [setCurrentComponent]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearchMedicine = useCallback(
    _.debounce(searchMedicine, 300, { leading: true }),
    []
  );

  const dispatchProduct = useCallback(
    async (product, node, isCellValueChanged, isScan) => {
      let rowIndex =
        node?.rowIndex || api?.getSelectedNodes()[0]?.rowIndex || 0;
      if (!productRowData.length) return;
      const updatedRowData = [...productRowData];
      updatedRowData[rowIndex === 0 ? updatedRowData.length - 1 : rowIndex] =
        product;
      dispatch({ type: "dispatch_product", payload: { product, rowIndex } });
      // if (!isScan) {
      setTimeout(() => {
        const currFocusedCell = api?.getFocusedCell();
        focusCell(
          api,
          isScan
            ? 0
            : currFocusedCell?.rowIndex || isCellValueChanged
            ? currFocusedCell?.rowIndex
            : api?.getDisplayedRowCount() - 1,
          isScan
            ? columnApi?.getColumn("remarks")
            : isCellValueChanged
            ? currFocusedCell.column
            : columnApi?.getColumn("remarks")
        );
      }, 300);
      // }
    },
    [api, columnApi, dispatch, productRowData]
  );

  const productSelectedHandler = useCallback(
    async (product, node, isScan, isCellValueChanged) => {
      if (!startDate)
        dispatch({
          type: "update_actors",
          payload: { startDate: moment().toISOString() },
        });

      let selectedProduct = product;
      if (!isCellValueChanged) {
        selectedProduct = createProductData(product);
      }
      api?.forEachNode((n) => {
        if (
          !node?.rowIndex &&
          n.data.medicineId === selectedProduct.medicineId
        ) {
          selectedProduct.enteredQty = parseInt(n.data.enteredQty);
          node = n;
        }
      });
      if (!node) {
        node = {
          rowIndex: api.getDisplayedRowCount(),
        };
        selectedProduct = {
          ...selectedProduct,
          enteredQty: 1,
        };
      }
      dispatchProduct(selectedProduct, node, isCellValueChanged, isScan);
      setShowMedicineAutocomplete(false);
      setSearchTerm("");
    },
    [api, dispatch, dispatchProduct, startDate]
  );

  const saveBatchHandler = useCallback(
    async (data) => {
      let medicine = await getMedicineDetails(`medicineId=${data.medicineId}`);
      searchBatchByIdView(`id=${data.batchId}`).then((response) => {
        productSelectedHandler(
          { ...medicine, batch: response },
          api.getRowNode(api.getSelectedNodes()[0].rowIndex)
        );
      });
    },
    [api, productSelectedHandler]
  );

  const searchByBarcode = useCallback(
    async (searchTerm, node, api) => {
      const response = await fetchBatchByBarcode(`query=${searchTerm}`);
      if (response?.length) {
        let modifiedData = {};
        let batchObject = await searchBatchInPurchase(
          `medicineId=${response[0].medicineId}&batchNumber=${response[0].batchNumber}`
        );
        let liveStock = await computeStock(
          `batchId=${response[0].batchId}&medicineId=${response[0].medicineId}`
        );
        let updatedNode = node;
        modifiedData = {
          ...batchObject.medicine,
          batch: {
            ...batchObject,
            currQuantity: liveStock.result.batchQty,
            currLoose: liveStock.result.batchLoose,
          },
          batches: response,
          isScan: true,
        };
        // api?.forEachNode((n) => {
        //   if (
        //     !updatedNode.rowIndex &&
        //     n.data.medicineId === modifiedData.medicineId &&
        //     n.data.batchId === modifiedData.batch.batchId
        //   ) {
        //     modifiedData.enteredQty = parseInt(n.data.enteredQty) + 1;
        //     updatedNode = n;
        //   }
        // });
        await productSelectedHandler(modifiedData, updatedNode, true);
        setSearchTerm("");
      } else {
        displayToast({
          type: "error",
          title: `No stock found with barcode ${searchTerm}`,
        });
        setShowMedicineAutocomplete(false);
      }
    },
    [productSelectedHandler]
  );

  const barcodeHandler = useCallback(
    async (barcode, node, api, columnApi) => {
      setShowLoading(true);
      await searchByBarcode(barcode, node, api);
      setSearchTerm("");
      setShowLoading(false);
    },
    [searchByBarcode, setShowLoading]
  );

  const productSearchHandler = useCallback(
    async (e) => {
      setSearchTerm(e.target.value);
      if (!barcode && !isNaN(e.target.value)) {
        focusCell(api, 0);
        setShowMedicineAutocomplete(false);
        return;
      }
      debounceSearchMedicine(e.target.value);
    },
    [api, barcode, debounceSearchMedicine]
  );

  const cellValueChanged = useCallback(
    (node, column) => {
      if (column.colId === "enteredQty" || column.colId === "enteredLoose") {
        productSelectedHandler(node.data, node, false, true);
      }
    },
    [productSelectedHandler]
  );

  const defFns = useMemo(() => {
    let defFnsObj = {
      medicineNameDetailed: {
        colSpan: (params) =>
          params.node.rowIndex !== 0
            ? 1
            : params.columnApi.getAllDisplayedColumns().length,
        cellEditorParams: (params) => {
          return {
            value:
              params.node.rowIndex !== 0
                ? getStringValue(params?.value)
                : searchTerm,
            placeholder: "Scan or search products you want to audit",
            isCancelAfterEnd: params.rowIndex === 0 ? false : true,
            onChange: productSearchHandler,
            InputProps: {
              startAdornment: params.rowIndex === 0 && (
                <InputAdornment position="start">
                  <img src={searchIcon} alt="search" />
                </InputAdornment>
              ),
              endAdornment:
                (params.data.currQuantity ||
                  (params.data.currLoose !== "" && params.rowIndex !== 0)) &&
                SHOW_CURRENT_STOCK_IN_STOCK_AUDIT ? (
                  <InputAdornment position="end">
                    <div
                      style={{
                        backgroundColor: "black",
                        color: "white",
                        fontSize: "16px",
                        fontWeight: "400",
                        textAlign: "center",
                        width: "60px",
                        padding: "5px",
                      }}
                    >
                      {params.data.currQuantity || 0}::
                      {params.data.currLoose || 0}
                    </div>
                  </InputAdornment>
                ) : null,
            },
          };
        },
        cellRendererFramework: (params) => {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
              className={params.rowIndex === 0 ? "ag-editable-cell" : ""}
            >
              {params.rowIndex === 0 ? (
                <div style={{ color: "#707070" }}>
                  <span style={{ margin: "0.5rem" }}>
                    <img src={searchIcon} alt="search" height="12px" />
                  </span>
                  Scan or search products you want to audit
                </div>
              ) : (
                <div
                  style={{
                    width: "90%",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {params.value}
                </div>
              )}
              {params.value !== null &&
              params.rowIndex !== 0 &&
              SHOW_CURRENT_STOCK_IN_STOCK_AUDIT ? (
                <span
                  style={{
                    backgroundColor: "black",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "400",
                    textAlign: "center",
                    width: "60px",
                  }}
                >
                  {params.data.currQuantity || 0}::
                  {params.data.currLoose || 0}
                </span>
              ) : null}
            </div>
          );
        },
        editable: (params) => {
          return params.node.rowIndex === 0;
        },
      },
      batchExpiry: {
        valueGetter: (params) =>
          params.data.batchExpiry
            ? moment(params.data.batchExpiry).format("MM/YY")
            : "",
      },
      enteredLoose: {
        headerName:
          IS_SELL_IN_LOOSE === "sometimes" || IS_SELL_IN_LOOSE === "always"
            ? "Scanned Loose"
            : "",
        hide:
          IS_SELL_IN_LOOSE === "sometimes" || IS_SELL_IN_LOOSE === "always"
            ? false
            : true,
        editable: (params) => {
          if (IS_SELL_IN_LOOSE === "always") return true;
          return params.data.isLoose;
        },
        cellEditorParams: (params) => {
          return {
            decimalScale: 0,
          };
        },
        cellRendererFramework: (params) => {
          return (
            <>
              {(params.data.isLoose || IS_SELL_IN_LOOSE === "always") && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                  className="ag-editable-cell"
                >
                  <div
                    style={{
                      width: "90%",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {params.value}
                  </div>
                </div>
              )}
            </>
          );
        },
      },
      enteredQty: {
        headerName: "Scanned Qty",
        hide: false,
        cellEditorParams: (params) => {
          return {
            isAllowed: withQtyValueCap,
            decimalScale: 0,
          };
        },
      },
    };
    return defFnsObj;
  }, [
    IS_SELL_IN_LOOSE,
    SHOW_CURRENT_STOCK_IN_STOCK_AUDIT,
    productSearchHandler,
    searchIcon,
    searchTerm,
    withQtyValueCap,
  ]);

  const addMedicine = useCallback(() => {
    medicineGridRef.current.blur(); // code to avoid focus lose on double click //this might get handled in ag grid commonly
    setTimeout(() => focusCell(api, 0, columnApi?.getColumn("remarks")), 1);
    // setAddClicked(false);
  }, [api, columnApi, medicineGridRef]);

  const removeMedicine = useCallback(() => {
    if (
      api?.getSelectedNodes()?.length === 0 ||
      !api.getSelectedNodes()[0].rowIndex
    ) {
      return displayToast({
        type: "error",
        title: "Please select a product to delete",
      });
    }
    let rowData = [...productRowData];
    let selectedIndex = api.getSelectedNodes()[0]?.rowIndex;
    rowData.splice(selectedIndex, 1);
    dispatch({
      type: "update_product_grid",
      payload: [...rowData],
    });
    setTimeout(() => {
      focusCell(
        api,
        selectedIndex === rowData.length ? selectedIndex - 1 : selectedIndex,
        columnApi?.getColumn("remarks")
      );
    }, 100);
  }, [api, columnApi, dispatch, productRowData]);

  const closeMedicineAc = useCallback(() => {
    setShowMedicineAutocomplete(false);
    setSearchTerm("");
    api.redrawRows({
      rowNodes: api?.getRowNode(api.getSelectedNodes()[0]?.rowIndex),
    });
    setTimeout(() => {
      focusCell(api, 0, columnApi?.getColumn("remarks"));
    }, 100);
  }, [api, columnApi]);

  const mainGridMarkers = useMemo(() => {
    return {
      altDown: "stock_audit_branches",
      altUp: "",
      right: "grid_stock_audit",
      down: "stock_audit_branches",
      up: "grid_stock_audit",
    };
  }, []);
  const gridEditingStopped = useCallback(
    (direction) => {
      if (api) stopEditing(api);
      if (mainGridMarkers[direction] === "grid_stock_audit") {
        const rowIndex =
          direction === "right" ? 0 : api.getDisplayedRowCount() - 1;
        selectRow(api, rowIndex);
        return medicineGridRef.current?.focus(
          direction !== "right",
          "medicineNameDetailed"
        );
      }
      if (direction && mainGridMarkers[direction]) {
        document
          .getElementById(mainGridMarkers[direction])
          .focus({ preventScroll: true });
        setCurrentComponent(mainGridMarkers[direction]);
      }
      if (api) stopEditing(api);
    },
    [api, mainGridMarkers, medicineGridRef, setCurrentComponent]
  );

  const tabHandler = useCallback(
    (shiftKey) => {
      if (!cellTabbed(api, shiftKey)) {
        gridEditingStopped(shiftKey ? "left" : "right");
      }
    },
    [api, gridEditingStopped]
  );

  const auditListener = useCallback(
    async (e) => {
      if (!api) return;
      let key = e.key.toLowerCase();
      let ctrlKey = e.ctrlKey;
      let shiftKey = e.shiftKey;
      let altKey = e.altKey;

      if (key === "tab") {
        if (shiftKey && api.getSelectedNodes()[0]?.rowIndex === 0) {
          return;
        } else {
          tabHandler(shiftKey);
          return;
        }
      }
      if (key === "enter") {
        const focusedCell = api.getFocusedCell();
        if (
          isBarcode(searchTerm) &&
          searchTerm.length >= MIN_BARCODE_LENGTH &&
          (focusedCell?.rowIndex === 0 ||
            focusedCell?.column?.colId === "medicineNameDetailed")
        ) {
          await barcodeHandler(
            searchTerm,
            api.getRowNode(focusedCell.rowIndex),
            api,
            columnApi
          );
          return setSearchTerm("");
        }
        tabHandler(shiftKey);
        return;
      }
      if (key === "arrowdown") {
        if (altKey) {
          gridEditingStopped("altDown");
          return;
        }
        if (
          !rowDown(
            api,
            ctrlKey,
            api.getFocusedCell().column.colDef.editable &&
              api.getFocusedCell().rowIndex === 0
              ? columnApi.getColumn("enteredQty")
              : api.getFocusedCell().column
          )
        )
          gridEditingStopped("down");
        return;
      }
      if (key === "arrowup") {
        if (altKey) {
          gridEditingStopped("altUp");
          return;
        }
        if (
          !rowUp(
            api,
            ctrlKey,
            api.getFocusedCell().column.colDef.editable &&
              api.getFocusedCell().rowIndex === 1
              ? columnApi.getColumn("medicineNameDetailed")
              : api.getFocusedCell().column
          )
        )
          gridEditingStopped("up");
        return;
      }
      if (key === "+") {
        addMedicine();
        return;
      }
      if (key === "delete") {
        removeMedicine();
        return;
      }
    },
    [
      addMedicine,
      api,
      barcodeHandler,
      columnApi,
      gridEditingStopped,
      removeMedicine,
      searchTerm,
      tabHandler,
    ]
  );

  useEffect(() => {
    if (currentComponent === "grid_stock_audit") {
      document.addEventListener("keyup", auditListener, false);
    }
    return () => {
      document.removeEventListener("keyup", auditListener, false);
    };
  }, [auditListener, currentComponent]);

  useEffect(() => {
    if (
      currentComponent !== "grid_stock_audit" &&
      !showMedicineAutocomplete &&
      showAuditorDetails &&
      api &&
      api.getSelectedNodes()?.length
    ) {
      gridEditingStopped();
    }
  }, [
    api,
    currentComponent,
    gridEditingStopped,
    showAuditorDetails,
    showMedicineAutocomplete,
  ]);

  useEffect(() => {
    if (!showMedicineAutocomplete) {
      setCurrentComponent("grid_stock_audit");
    }
  }, [setCurrentComponent, showMedicineAutocomplete]);

  useEffect(() => {
    if (api) {
      initializeColumnDefs(
        api,
        STOCK_AUDIT_COLUMN_DEFS,
        stock_audit_col_defs,
        defFns
      );
    }
  }, [api, defFns]);

  useEffect(() => {
    if (barcode) {
      setBarcode("");
      setTimeout(() => {
        setSearchTerm("");
      }, 100);
    }
  }, [barcode, setBarcode]);

  useEffect(() => {
    let enterQty = 0;
    let enterLoose = 0;
    productRowData.forEach((item) => {
      enterQty += item?.enteredQty ? item?.enteredQty : 0;
      enterLoose += item?.enteredLoose ? item?.enteredLoose : 0;
    });
    dispatch({
      type: "update_bill_summary",
      payload: {
        totalItem: productRowData?.length - 1,
        totalQty: enterQty,
        totalLoose: enterLoose,
      },
    });
    if (lastProductSelectedIndex !== 0) {
      setLastScannedMedicine(productRowData[lastProductSelectedIndex]);
    } else {
      setLastScannedMedicine(null);
    }
  }, [dispatch, lastProductSelectedIndex, productRowData]);

  return (
    <>
      <div
        id="sale_medicine_container"
        className={`${classes["medicine-grid-parent"]}`}
      >
        <div
          className={`${showMedicineAutocomplete ? classes["minimized"] : ""} ${
            classes["medicine-grid-sales-bill-container"]
          }`}
        >
          <div className={`${classes["medicine-grid-container"]}`}>
            <div className={classes["medicine-grid"]}>
              <Aggrid
                id="grid_stock_audit"
                ref={medicineGridRef}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                rowData={productRowData}
                animateRows={false}
                frameworkComponents={{
                  cellTextEditor: CellTextEditor,
                  cellNumberEditor: CellNumberEditor,
                }}
                onSelectionChanged={(params) => {
                  // if (params.api.getSelectedNodes()[0]?.rowIndex) {
                  //   setCurrentProduct(params.api.getSelectedNodes()[0].data);
                  // } else {
                  //   setCurrentProduct(null);
                  // }
                  params.api.resetRowHeights();
                }}
                getRowClass={(params) => {
                  let returnClass = "";
                  if (params.data.checked) {
                    returnClass += "checked ";
                  }
                  return returnClass;
                }}
                onCellFocused={(params) => {
                  if (showMedicineAutocomplete || autocompleteOpen) {
                    return;
                  } else {
                    if (params.column.colDef.editable) {
                      cellClicked(params.api, params.rowIndex, params.column);
                    } else {
                      cellClicked(
                        params.api,
                        params.rowIndex,
                        params.columnApi.getColumn("enteredQty") //any editable column or null if no editable columns
                      );
                    }
                    focusField(
                      "grid_stock_audit",
                      true,
                      params.api.getSelectedNodes()[0]?.rowIndex || 0,
                      params.api.getFocusedCell().column.colId
                    );
                  }
                }}
              />
            </div>
            <div className={classes["medicine-grid-cta-container"]}>
              <div className={classes["row-level-ctas"]}>
                <div className={classes["row-level-cta"]}>
                  <Button
                    label="ADD"
                    startIcon={<Shortcut keys={["+"]} />}
                    onClick={addMedicine}
                  />
                  <div className={classes["cta-divider"]} />
                </div>
                <div className={classes["row-level-cta"]}>
                  <Button
                    label="Delete"
                    startIcon={<Shortcut keys={["Del"]} />}
                    onClick={removeMedicine}
                  />
                </div>
              </div>
              {lastScannedMedicine && (
                <div className={classes["last-selected-medicine-container"]}>
                  <div className={classes["infobar-left-container"]}>
                    <div
                      className={`${classes["medicine-grid-infobar-inner-container"]} ${classes["productId"]}`}
                    >
                      <div
                        className={classes["medicine-grid-infobar-inner-key"]}
                      >
                        Last added product:
                      </div>
                      <div
                        className={classes["medicine-grid-infobar-inner-value"]}
                      >
                        {lastScannedMedicine?.medicineNameDetailed}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Autocomplete
        show={showMedicineAutocomplete}
        closeAutocomplete={closeMedicineAc}
      >
        <SelectProductAutocomplete
          searchTerm={searchTerm}
          ref={medicineGridRef}
          medicineSelected={(product) => {
            productSelectedHandler(product);
          }}
          onEscape={closeMedicineAc}
          showCurrentStock={SHOW_CURRENT_STOCK_IN_STOCK_AUDIT}
          onSave={(data) => {
            saveBatchHandler(data);
          }}
          isStockAudit={true}
        />
      </Autocomplete>
    </>
  );
};

export default StockAuditProductGrid;
